import React from "react";
import {
  ArrowLeft,
  Bot,
  Check,
  Copy,
  Gamepad2,
  Globe,
  KeyRound,
  Rocket,
  Terminal,
} from "lucide-react";
import TerminalCast from "./TerminalCast";
import { LlmQuickstart } from "./LlmQuickstart";
import { CodeBlock } from "./CodeBlock";

// Bespoke /cli page: install, a real recorded demo (agent builds a game, the
// CLI deploys it), and the full command reference. The cast file is a genuine
// asciinema v2 recording produced by scripts/record-cli-cast.mjs.

const INSTALL_COMMAND = "curl -fsSL https://app.nz/cli.sh | sh";

interface CommandRow {
  cmd: string;
  desc: string;
}

interface CommandGroup {
  title: string;
  blurb?: string;
  rows: CommandRow[];
}

const COMMAND_GROUPS: CommandGroup[] = [
  {
    title: "Auth & account",
    rows: [
      { cmd: "app login", desc: "Sign in with browser OAuth + PKCE" },
      { cmd: "app auth status", desc: "Show login state" },
      { cmd: "app whoami", desc: "Show the signed-in account" },
      { cmd: "app keys create --name deploy-bot", desc: "Mint an API key (secret shown once)" },
      { cmd: "app keys rotate", desc: "Rotate this call's key" },
    ],
  },
  {
    title: "Apps hosting",
    blurb: "Heroku-style deploys driven by appnz.yaml. Static buckets or server runtimes at <slug>.app.nz.",
    rows: [
      { cmd: "app apps init --runtime static", desc: "Scaffold appnz.yaml here" },
      { cmd: "app apps deploy", desc: "Package, build, release this directory" },
      { cmd: "app apps deploy ./game --app neon-snake", desc: "Deploy a directory to a named app" },
      { cmd: "app apps logs neon-snake", desc: "Build + runtime logs for the latest release" },
      { cmd: "app apps deploys neon-snake", desc: "Release history" },
      { cmd: "app apps open neon-snake", desc: "Open the live app" },
    ],
  },
  {
    title: "Static sites",
    blurb: "For plain files: mirror a directory to a hosted site.",
    rows: [
      { cmd: "app sites create my-game --title \"My Game\"", desc: "Create a hosted site" },
      { cmd: "app sites deploy my-game ./dist", desc: "Publish a directory (uploads + prunes)" },
      { cmd: "app sites firewall my-game list", desc: "Per-site rate limits and blocks" },
      { cmd: "app analytics top my-game", desc: "Traffic for a deployed site" },
    ],
  },
  {
    title: "Coding agents",
    rows: [
      { cmd: "app agent", desc: "Local coding agent in this directory (interactive)" },
      { cmd: "app agent \"why is this test flaky?\"", desc: "One-shot local run, streamed" },
      { cmd: "app agent auto \"<goal>\" --verify \"npm test\" --commit", desc: "Plans, verifies, commits; a second model supervises" },
      { cmd: "app agent resume", desc: "Continue the last local session" },
      { cmd: "app agent doctor", desc: "Storage failover chain, model routes, plan sync, skills" },
      { cmd: "app agent auth sync", desc: "Bill gateway calls to your own ChatGPT plan" },
      { cmd: "app agent run --prompt \"...\" --repo owner/name", desc: "Launch a cloud coding agent" },
      { cmd: "app agent list", desc: "Task history with status" },
      { cmd: "app agent get <id>", desc: "One task: status, PR link, logs" },
      { cmd: "app agent apply <id> --dir ./out", desc: "Write changed files locally" },
      { cmd: "app agent cancel <id>", desc: "Stop a running task" },
      { cmd: "app agents-sdk run \"...\" --repo owner/name", desc: "Provider-aware cloud coding task" },
      { cmd: "app scheduler create --kind agent --cron \"0 9 * * *\" --prompt \"...\"", desc: "Agents on a schedule" },
    ],
  },
  {
    title: "Models & chat",
    rows: [
      { cmd: "app chat \"explain this error\" --model app/auto", desc: "Streamed chat from the gateway" },
      { cmd: "app chat \"latest on X\" --web", desc: "Web-grounded answers" },
      { cmd: "app gateway models", desc: "Every routable model" },
      { cmd: "app ra1 generate --prompt \"neon city\"", desc: "Image generation" },
      { cmd: "app papers search \"diffusion transformers\" --type github_code", desc: "Shared paper, code, repo, and web search" },
    ],
  },
  {
    title: "Repos, PRs & CI",
    blurb: "GitHub parity: repo, issue, pr, release, label, secret, ruleset, gist. Check coverage with app parity.",
    rows: [
      { cmd: "app repo create my-game --public --clone", desc: "Create + clone over smart-HTTP" },
      { cmd: "app pr create owner/name --title \"...\" --head feat", desc: "Open a pull request" },
      { cmd: "app ci run <pipeline-id> --watch", desc: "Start CI and poll to completion" },
      { cmd: "app run list", desc: "CI/agent run history" },
    ],
  },
  {
    title: "Addons",
    blurb: "Managed services attached to a repo or deploy. Env vars (DATABASE_URL, GOBED_SEARCH_URL, APP_ADDON_*) injected into every runtime.",
    rows: [
      { cmd: "app addons create --type postgres --plan starter --attach-default", desc: "Managed Postgres (pgvector HNSW + PostGIS) as DATABASE_URL" },
      { cmd: "app addons create --type gobed --plan gpu", desc: "gobed GPU vector search (cuVS CAGRA)" },
      { cmd: "app addons list", desc: "All addons with plan, region, status" },
      { cmd: "app addons env <addon-id>", desc: "Print the injected environment" },
      { cmd: "app addons delete <addon-id> --force", desc: "Destroy an addon and its data" },
    ],
  },
  {
    title: "Machines & data",
    rows: [
      { cmd: "app instance create --type cpx21", desc: "Rent a CPU/GPU machine" },
      { cmd: "app ssh <id>", desc: "SSH in (keys auto-injected)" },
      { cmd: "app datasets push data.csv", desc: "Upload a dataset" },
      { cmd: "app notebook create analysis", desc: "Hosted marimo notebook" },
      { cmd: "app queue send jobs \"encode video 42\"", desc: "Queue a message" },
    ],
  },
  {
    title: "Billing & escape hatch",
    rows: [
      { cmd: "app usage", desc: "Model + server spend, credit balance" },
      { cmd: "app credits buy --packs 2", desc: "Prepaid credit packs" },
      { cmd: "app api GET /api/me", desc: "Call any endpoint directly" },
      { cmd: "app completion bash", desc: "Shell completions (bash/zsh/fish)" },
      { cmd: "app alias set dg \"apps deploy ./game\"", desc: "Your own shorthands" },
    ],
  },
];

const APPNZ_YAML = `# appnz.yaml: one file describes the deploy
name: neon-snake
runtime: static     # or: server
output: .           # dir published to neon-snake.app.nz

# server runtimes add:
# build:
#   command: npm ci && npm run build
# run:
#   command: node server.js
#   port: 3000`;

function CopyableCommand({ command, className = "" }: { command: string; className?: string }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className={`group flex w-full min-w-0 max-w-full items-center gap-3 rounded-xl border border-white/12 bg-[#070b14] px-4 py-3 text-left font-mono text-sm text-white/85 transition hover:border-white/30 ${className}`}
    >
      <span className="text-cyan-300/80">$</span>
      <span className="min-w-0 flex-1 truncate">{command}</span>
      {copied ? (
        <Check className="h-4 w-4 shrink-0 text-emerald-300" />
      ) : (
        <Copy className="h-4 w-4 shrink-0 text-white/35 transition group-hover:text-white/70" />
      )}
    </button>
  );
}

function CommandGroupCard({ group }: { group: CommandGroup }) {
  return (
    <section className="min-w-0 rounded-xl border border-white/10 bg-white/[0.035] p-5">
      <h3 className="font-display text-lg font-bold">{group.title}</h3>
      {group.blurb && <p className="mt-1.5 text-sm leading-6 text-white/50">{group.blurb}</p>}
      <div className="mt-3 grid gap-1.5">
        {group.rows.map((row) => (
          <CommandRowItem key={row.cmd} row={row} />
        ))}
      </div>
    </section>
  );
}

function CommandRowItem({ row }: { row: CommandRow }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(row.cmd);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title="Copy command"
      className="group grid min-w-0 gap-0.5 rounded-lg border border-white/[0.07] bg-black/60 px-3 py-2 text-left transition hover:border-white/25"
    >
      <span className="flex min-w-0 items-center gap-2">
        <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-white/85">{row.cmd}</code>
        {copied ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-300" />
        ) : (
          <Copy className="h-3.5 w-3.5 shrink-0 text-white/0 transition group-hover:text-white/50" />
        )}
      </span>
      <span className="text-xs leading-5 text-white/44">{row.desc}</span>
    </button>
  );
}

const quickstart = [
  {
    icon: KeyRound,
    title: "1. Sign in",
    body: "Run app login. App neocloud CLI, API, desktop cloud bridge, Git credentials, and MCP proxy all work together.",
  },
  {
    icon: Bot,
    title: "2. Build with an agent",
    body: "app agent runs a coding agent in your terminal. app agent run sends the task to a cloud agent that branches, codes, tests, and opens a PR.",
  },
  {
    icon: Rocket,
    title: "3. Deploy",
    body: "app apps deploy packages the directory, runs the build server-side, and serves it at <name>.app.nz over HTTPS. No DNS, no extra YAML.",
  },
];

export const CliPage: React.FC = () => {
  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1400px] min-w-0">
        <a href="/" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-white/48 hover:text-white">
          <ArrowLeft size={16} />
          Back to overview
        </a>

        <div className="mb-10 grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="min-w-0">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/54">
              <Terminal size={14} />
              app cli
            </div>
            <h1 className="font-display text-3xl font-bold leading-tight tracking-tight sm:text-5xl">
              The app.nz CLI.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-white/58">
              One binary runs coding agents, deploys apps and sites, routes 100+ models, manages repos and CI,
              rents machines, and calls every endpoint with <code className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-sm text-white">app api</code>.
              Everything below is real output.
            </p>
            <div className="mt-6 grid gap-3">
              <CopyableCommand command={INSTALL_COMMAND} />
              <p className="text-xs text-white/45">
                macOS, Linux, and Windows builds, or <code className="font-mono">go install github.com/lee101/app/cmd/app@latest</code>.
                Source at{" "}
                <a href="/repos/leepenkman/app-cli" className="text-white/70 underline decoration-white/30 hover:text-white">
                  /repos/leepenkman/app-cli
                </a>.
              </p>
            </div>
          </div>

          <div className="min-w-0">
            <TerminalCast
              src="/casts/cli-deploy-game.cast"
              title="app: build and ship a game"
              rows={19}
            />
            <p className="mt-2.5 flex flex-wrap items-center gap-x-2 text-xs text-white/45">
              <Gamepad2 className="h-3.5 w-3.5" />
              Recorded session, sped up: a cloud agent writes the game, <code className="font-mono">app apps deploy</code> ships it.
              <a href="https://neon-snake.app.nz/" target="_blank" rel="noreferrer" className="text-cyan-300/80 underline decoration-cyan-300/30 hover:text-cyan-200">
                Play the result →
              </a>
            </p>
          </div>
        </div>

        <div className="mb-10 grid gap-3 sm:grid-cols-3">
          {quickstart.map((step) => (
            <div key={step.title} className="rounded-xl border border-white/10 bg-white/[0.035] p-5">
              <step.icon className="h-5 w-5 text-white/54" />
              <h3 className="mt-3 font-display text-base font-bold">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-6 text-white/52">{step.body}</p>
            </div>
          ))}
        </div>

        <LlmQuickstart className="mb-10" />

        <section className="mb-10">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-200/55">Recorded workflows</p>
              <h2 className="mt-1 font-display text-2xl font-bold tracking-tight">Agent and model workflows.</h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-white/48">Read-only sessions recorded from the real CLI and the live public catalog.</p>
          </div>
          <div className="grid gap-5 xl:grid-cols-2">
            <div className="min-w-0">
              <TerminalCast src="/casts/cli-agent-workflow.cast" title="app: brief and choose a coding agent" rows={17} />
              <p className="mt-2 text-xs leading-5 text-white/45">Write a bounded brief, inspect the available agent providers, then pick a task prompt.</p>
            </div>
            <div className="min-w-0">
              <TerminalCast src="/casts/cli-model-routing.cast" title="app: route models without rewriting your client" rows={17} />
              <p className="mt-2 text-xs leading-5 text-white/45">Start with the router, then filter the live model catalog by provider and keep a fallback ready.</p>
            </div>
          </div>
        </section>

        <div className="mb-10 grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="min-w-0 rounded-xl border border-white/10 bg-white/[0.035] p-5">
            <div className="mb-2 flex items-center gap-2">
              <Globe className="h-4.5 w-4.5 text-white/54" size={18} />
              <h2 className="font-display text-xl font-bold">Hosting: one manifest, two runtimes</h2>
            </div>
            <p className="mb-4 max-w-2xl text-sm leading-7 text-white/58">
              <code className="font-mono text-white/80">app apps deploy</code> reads <code className="font-mono text-white/80">appnz.yaml</code> (or
              deploys a bare directory as a static app), uploads a source tarball, builds on
              the server, and publishes to <code className="font-mono text-white/80">&lt;name&gt;.app.nz</code>. Server
              runtimes get a managed process, injected <code className="font-mono text-white/80">PORT</code>, per-deploy
              env vars, and <code className="font-mono text-white/80">app apps logs</code>.
            </p>
            <CodeBlock code={APPNZ_YAML} language="yaml" preClassName="!text-[13px] !leading-6" />
          </section>
          <section className="min-w-0 rounded-xl border border-white/10 bg-white/[0.035] p-5">
            <h2 className="font-display text-xl font-bold">Scripting and CI</h2>
            <p className="mb-4 mt-2 max-w-2xl text-sm leading-7 text-white/58">
              Every command takes <code className="font-mono text-white/80">--json</code>, reads{" "}
              <code className="font-mono text-white/80">APP_API_KEY</code> / <code className="font-mono text-white/80">APP_BASE_URL</code>{" "}
              from the environment, and exits non-zero on failure. Runs in CI, cron, or an agent's toolbox.
            </p>
            <div className="grid gap-2">
              <CopyableCommand command="app --json apps deploys neon-snake | jq '.deploys[0].status'" />
              <CopyableCommand command={'app chat "summarize today\'s deploy logs" --model app/auto'} />
              <CopyableCommand command={'app api POST /api/queues/jobs/messages \'{"text":"encode 42"}\''} />
            </div>
            <p className="mt-4 text-sm leading-6 text-white/52">
              <code className="font-mono text-white/80">app parity</code> prints the{" "}
              <code className="font-mono text-white/80">gh</code> coverage manifest: repo, issue, pr, release,
              secret, and ruleset map to the <code className="font-mono text-white/80">gh</code> commands.
            </p>
          </section>
        </div>

        <h2 className="mb-4 font-display text-2xl font-bold tracking-tight">Command reference</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {COMMAND_GROUPS.map((group) => (
            <CommandGroupCard key={group.title} group={group} />
          ))}
        </div>

        <p className="mt-8 text-sm text-white/50">
          Full API behind every command:{" "}
          <a href="/docs" className="text-white/75 underline decoration-white/30 hover:text-white">
            /docs
          </a>
          {" · "}agent guide:{" "}
          <a href="/llms-full.txt" className="font-mono text-white/75 underline decoration-white/30 hover:text-white">
            /llms-full.txt
          </a>
        </p>
      </div>
    </main>
  );
};

export default CliPage;
